import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ScalesIcon } from '@phosphor-icons/react';
import { Button, Dialog } from '@origo/origo-ui';
import { EmptyState } from '@geodata/georg';
import PlanDetailHeader from '../../components/PlanDetailHeader';
import DispensationGroupHeader from './components/DispensationGroupHeader';
import DispensationListItem from './components/DispensationListItem';
import NewDispensationDialog from './components/NewDispensationDialog';
import type { DispensationFieldErrors } from './components/DispensationForm';
import type { DispensationGroup, DispensationItem, DispensasjonFormValues, FieldChangeHandler } from '../../types';
import styles from './DispensationsPage.module.css';

export interface NewDispensationState {
    open: boolean;
    values: DispensasjonFormValues;
    fieldErrors: DispensationFieldErrors;
    isSaving: boolean;
}

interface DispensationsPageProps {
    groups: DispensationGroup[];
    openGroups: Set<DispensationGroup['key']>;
    onToggleGroup: (key: DispensationGroup['key']) => void;

    newDispensation: NewDispensationState;
    onNewOpen: () => void;
    onNewClose: () => void;
    onNewChange: FieldChangeHandler<DispensasjonFormValues>;
    onNewSave: () => void;

    // Rediger
    fieldErrorsById: Record<string, DispensationFieldErrors | undefined>;
    onItemSave: (id: string, values: DispensasjonFormValues) => void;
    /** Åpner sidepanelet med tilknyttede dokumenter for en dispensasjon (F12.4). */
    onShowDocuments: (dispensasjonId: string) => void;
    isSaving?: boolean;

    // Slett (#14731)
    onItemDeleteRequest: (id: string) => void;
    pendingDelete: DispensationItem | null;
    /** Dispensasjonen har tilknyttede dokumenter og kan ikke slettes. */
    deleteBlocked: boolean;
    onDeleteConfirm: () => void;
    onDeleteCancel: () => void;
    isDeleting?: boolean;
}

interface DispensationGroupListProps {
    groups: DispensationGroup[];
    openGroups: Set<DispensationGroup['key']>;
    onToggleGroup: (key: DispensationGroup['key']) => void;
    fieldErrorsById: Record<string, DispensationFieldErrors | undefined>;
    onItemSave: (id: string, values: DispensasjonFormValues) => void;
    /** Åpner sidepanelet med tilknyttede dokumenter for en dispensasjon (F12.4). */
    onShowDocuments: (dispensasjonId: string) => void;
    onItemDeleteRequest: (id: string) => void;
    isSaving?: boolean;
    t: TFunction;
}

/** Memoized slik at tastetrykk i "ny dispensasjon"-dialogen ikke rerendrer hele listen. */
const DispensationGroupList: React.FC<DispensationGroupListProps> = React.memo(
    ({ groups, openGroups, onToggleGroup, fieldErrorsById, onItemSave, onShowDocuments, onItemDeleteRequest, isSaving, t }) => (
        <>
            {groups.map(group => (
                <DispensationGroupHeader
                    key={group.key}
                    label={t(`dispensations.groups.${group.key}`)}
                    count={group.count}
                    open={openGroups.has(group.key)}
                    onToggle={() => onToggleGroup(group.key)}
                >
                    {group.items.length === 0 ? (
                        <p className={styles.empty}>{t('dispensations.emptyGroup')}</p>
                    ) : (
                        <ul className={styles.list}>
                            {group.items.map(item => (
                                <DispensationListItem
                                    key={item.id}
                                    item={item}
                                    fieldErrors={fieldErrorsById[item.id]}
                                    onSave={onItemSave}
                                    onShowDocuments={onShowDocuments}
                                    onDeleteRequest={onItemDeleteRequest}
                                    isSaving={isSaving}
                                />
                            ))}
                        </ul>
                    )}
                </DispensationGroupHeader>
            ))}
        </>
    )
);

const DispensationsPage: React.FC<DispensationsPageProps> = ({
    groups,
    openGroups,
    onToggleGroup,
    newDispensation,
    onNewOpen,
    onNewClose,
    onNewChange,
    onNewSave,
    fieldErrorsById,
    onItemSave,
    onShowDocuments,
    onItemDeleteRequest,
    pendingDelete,
    deleteBlocked,
    onDeleteConfirm,
    onDeleteCancel,
    isDeleting,
    isSaving,
}) => {
    const { t } = useTranslation();

    const isEmpty = groups.length === 0 || groups.every(g => g.items.length === 0);

    return (
        <article className={styles.page}>
            <PlanDetailHeader
                title={t('dispensations.title')}
                subtitle={t('dispensations.subtitle')}
                actions={
                    !isEmpty ? (
                        <Button variant="secondary" size="md" onClick={onNewOpen}>
                            {t('dispensations.newDispensation')}
                        </Button>
                    ) : undefined
                }
            />
            <div className={styles.body}>
                {isEmpty ? (
                    <EmptyState
                        icon={<ScalesIcon size={32} weight="duotone" />}
                        title={t('dispensations.empty.title')}
                        description={t('dispensations.empty.description')}
                        action={
                            <Button variant="primary" size="lg" onClick={onNewOpen}>
                                {t('dispensations.empty.action')}
                            </Button>
                        }
                    />
                ) : (
                    <DispensationGroupList
                        groups={groups}
                        openGroups={openGroups}
                        onToggleGroup={onToggleGroup}
                        fieldErrorsById={fieldErrorsById}
                        onItemSave={onItemSave}
                        onShowDocuments={onShowDocuments}
                        onItemDeleteRequest={onItemDeleteRequest}
                        isSaving={isSaving}
                        t={t}
                    />
                )}
            </div>
            <NewDispensationDialog
                {...newDispensation}
                onChange={onNewChange}
                onClose={onNewClose}
                onSave={onNewSave}
            />
            <Dialog
                open={pendingDelete !== null}
                onClose={onDeleteCancel}
                title={t(deleteBlocked ? 'dispensations.delete.blockedTitle' : 'dispensations.delete.title')}
                footer={
                    <div className={styles.dialogFooter}>
                        {deleteBlocked ? (
                            <Button variant="secondary" onClick={onDeleteCancel}>
                                {t('common.close')}
                            </Button>
                        ) : (
                            <>
                                <Button variant="secondary" onClick={onDeleteCancel} disabled={isDeleting}>
                                    {t('common.cancel')}
                                </Button>
                                <Button variant="danger" onClick={onDeleteConfirm} disabled={isDeleting}>
                                    {t('dispensations.delete.confirm')}
                                </Button>
                            </>
                        )}
                    </div>
                }
            >
                <p>
                    {deleteBlocked
                        ? t('dispensations.delete.blockedBody', { count: pendingDelete?.documentCount ?? 0 })
                        : t('dispensations.delete.body', { navn: pendingDelete?.tittel ?? '' })}
                </p>
            </Dialog>
        </article>
    );
};

export default DispensationsPage;
